
import React from 'react';
import { useRoleDashboardStats } from '@/hooks/use-role-dashboard-stats';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, Calendar, Users, Building2, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SellerDashboard: React.FC = () => {
  const stats = useRoleDashboardStats('seller');
  const navigate = useNavigate();

  if (stats.isLoading) {
    return <div className="p-6">Loading seller dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Seller Dashboard</h1>
        <p className="text-muted-foreground mt-1">Sales operations and relationship management</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Procurements</CardTitle>
            <Package className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.activeProcurements || 0}</div>
            <p className="text-xs text-muted-foreground">In progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Procurements</CardTitle>
            <Package className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.monthlyProcurements || 0}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Harvests</CardTitle>
            <Calendar className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.upcomingHarvests || 0}</div>
            <p className="text-xs text-muted-foreground">Next 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Suppliers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSuppliers || 0}</div>
            <p className="text-xs text-muted-foreground">Available suppliers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Agents</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalAgents || 0}</div>
            <p className="text-xs text-muted-foreground">Available agents</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Button onClick={() => navigate('/procurements/new')} className="h-20 flex flex-col gap-2">
          <Package className="h-6 w-6" />
          <span>New Procurement</span>
        </Button>
        
        <Button onClick={() => navigate('/advance-payments')} variant="outline" className="h-20 flex flex-col gap-2">
          <DollarSign className="h-6 w-6" />
          <span>Advance Payments</span>
        </Button>
        
        <Button onClick={() => navigate('/suppliers')} variant="outline" className="h-20 flex flex-col gap-2">
          <Users className="h-6 w-6" />
          <span>Manage Suppliers</span>
        </Button>
        
        <Button onClick={() => navigate('/agents')} variant="outline" className="h-20 flex flex-col gap-2">
          <Building2 className="h-6 w-6" />
          <span>Manage Agents</span>
        </Button>
        
        <Button onClick={() => navigate('/harvests')} variant="outline" className="h-20 flex flex-col gap-2">
          <Calendar className="h-6 w-6" />
          <span>View Harvests</span>
        </Button>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Procurements</CardTitle>
            <Button variant="outline" onClick={() => navigate('/procurement')}>View All</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recentProcurements?.map((procurement: any) => (
                <div key={procurement.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{procurement.product_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">
                      {procurement.total_quantity || 0} kg • Br{procurement.total_amount?.toLocaleString() || 0}
                    </p>
                  </div>
                  <Badge variant={
                    procurement.status === 'completed' ? 'default' :
                    procurement.status === 'pending_finance' ? 'secondary' :
                    procurement.status === 'rejected' ? 'destructive' : 'outline'
                  }>
                    {procurement.status}
                  </Badge>
                </div>
              )) || <p className="text-muted-foreground">No recent procurements</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Upcoming Harvests</CardTitle>
            <Button variant="outline" onClick={() => navigate('/harvests')}>View All</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.upcomingHarvestsList?.map((harvest: any) => (
                <div key={harvest.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{harvest.product_name || 'N/A'}</p>
                    <p className="text-sm text-muted-foreground">
                      Expected: {harvest.expected_date ? new Date(harvest.expected_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <Badge variant="secondary">{harvest.status}</Badge>
                </div>
              )) || <p className="text-muted-foreground">No upcoming harvests</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SellerDashboard;
